import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import HomePage from '@/components/HomePage';
import AnnouncementsPage from '@/components/AnnouncementsPage';
import AppointmentsPage from '@/components/AppointmentsPage';
import ProductsPage from '@/components/ProductsPage';
import AboutPage from '@/components/AboutPage';
import ProfilePage from '@/components/ProfilePage';
import AdminPanel from '@/components/AdminPanel';
import RewardShop from '@/components/RewardShop';

import Avatar from '@/components/Avatar';
import MusicButton from '@/components/MusicButton';
import axios from 'axios';
import { Home, Calendar, Crown, Bell, User, Menu, X, ShoppingBag, Info, Shield, Download } from 'lucide-react';
import InAppNotification from '@/components/InAppNotification';
import InstallModal from '@/components/InstallModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
// BRAND COLOR - Premium Gray-Beige (from barbershop interior)
const BRAND = '#A9B2B9'; // Cool Gray
const GOLD = BRAND; // Keep GOLD alias for backward compatibility
const STORAGE_KEY = 'dimakas_last_read_announcements';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function Dashboard() {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('home');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifBanner, setNotifBanner] = useState(false);
  const [notifSubscribed, setNotifSubscribed] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [bubblePosition, setBubblePosition] = useState({ left: 0, width: 0 });
  const [inAppNotifications, setInAppNotifications] = useState([]);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);

  // Fetch announcements and calculate unread count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/announcements`, { withCredentials: true });
      const lastRead = localStorage.getItem(STORAGE_KEY);
      const lastReadDate = lastRead ? new Date(lastRead) : new Date(0);
      const unread = res.data.filter(a => new Date(a.created_at) > lastReadDate).length;
      setUnreadCount(unread);
    } catch { /* silent */ }
  }, [API, STORAGE_KEY]);



  // PWA Install Prompt Handler
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      // Show the install button
      setShowInstallButton(true);
      console.log('[PWA] Install prompt available');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setShowInstallButton(false);
      console.log('[PWA] Already installed');
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    // Check if browser supports native install prompt
    if (deferredPrompt) {
      try {
        // Show the native install prompt
        deferredPrompt.prompt();
        
        // Wait for the user to respond
        const { outcome } = await deferredPrompt.userChoice;
        console.log(`[PWA] User response: ${outcome}`);
        
        // Clear the deferredPrompt
        setDeferredPrompt(null);
        
        if (outcome === 'accepted') {
          console.log('[PWA] App installed successfully');
        }
      } catch (error) {
        console.error('[PWA] Install prompt error:', error);
        // Fallback to modal on error
        setShowInstallModal(true);
      }
    } else {
      // No native prompt available - show custom modal with instructions
      console.log('[PWA] No native prompt - showing custom modal');
      setShowInstallModal(true);
    }
  };

  // Show in-app notification
  const showInAppNotification = useCallback((title, body) => {
    const notification = {
      id: Date.now(),
      title,
      body,
      timestamp: new Date().toISOString()
    };
    
    setInAppNotifications(prev => [...prev, notification]);
    
    // Also try browser notification as backup
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      try {
        new Notification(title, {
          body,
          icon: '/logo192.png',
          badge: '/logo192.png',
          requireInteraction: true,
          vibrate: [300, 200, 300],
          tag: 'chat-' + Date.now()
        });
      } catch (e) {
        console.error('Browser notification failed:', e);
      }
    }
  }, [setInAppNotifications]);

  const removeInAppNotification = useCallback((id) => {
    setInAppNotifications(prev => prev.filter(n => n.id !== id));
  }, []);



  useEffect(() => {
    setupPushNotifications();
    fetchUnreadCount();
    
    // Listen for service worker messages (push notification clicks)
    const handleServiceWorkerMessage = (event) => {
      console.log('📱 Dashboard received SW message:', event.data);
      
      // Show alert when notification is shown (for debugging)
      if (event.data && event.data.type === 'NOTIFICATION_SHOWN') {
        console.log('✅ Notification was shown!', event.data.title);
        // Optional: Show in-app indicator
        // alert(`Notification shown: ${event.data.title}`);
      }
    };
    
    navigator.serviceWorker?.addEventListener('message', handleServiceWorkerMessage);
    
    // Poll every 30s for new announcements
    const interval = setInterval(async () => {
      await fetchUnreadCount();
    }, 30000); // Check every 30 seconds
    
    return () => {
      clearInterval(interval);
      navigator.serviceWorker?.removeEventListener('message', handleServiceWorkerMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setupPushNotifications = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || typeof Notification === 'undefined') return;
    
    const permission = Notification.permission;
    
    // If already granted, subscribe immediately
    if (permission === 'granted') { 
      await doSubscribe(); 
      setNotifSubscribed(true); 
      return; 
    }
    
    // If denied, don't show banner
    if (permission === 'denied') return;
    
    // If default (not asked yet), show banner immediately
    if (permission === 'default') {
      setNotifBanner(true);
      return;
    }
  };

  const doSubscribe = async () => {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;
      const vapidRes = await axios.get(`${API}/push/vapid-key`);
      const applicationServerKey = urlBase64ToUint8Array(vapidRes.data.public_key);
      const existingSub = await registration.pushManager.getSubscription();
      if (existingSub) await existingSub.unsubscribe();
      const subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
      await axios.post(`${API}/push/subscribe`, subscription.toJSON(), { withCredentials: true });
      setNotifSubscribed(true);
      console.log('✅ Push notifications enabled successfully!');
    } catch (err) { 
      console.error('Push subscription failed:', err); 
    }
  };

  const handleEnableNotifications = async () => {
    if (typeof Notification === 'undefined') return;
    setNotifBanner(false);
    const permission = await Notification.requestPermission();
    if (permission === 'granted') await doSubscribe();
  };

  const handleDismissNotifBanner = () => {
    setNotifBanner(false);
    // Don't save to localStorage - allow showing again next time
  };

  // Bottom nav tabs — new order
  const bottomTabs = [
    { id: 'home', label: 'Αρχική', icon: Home },
    { id: 'appointments', label: 'Ραντεβού', icon: Calendar },
    { id: 'loyalty', label: 'Elite', icon: Crown, gold: true },
    { id: 'announcements', label: 'Ανακοινώσεις', icon: Bell, badge: unreadCount > 0 ? unreadCount : null },
    { id: 'profile', label: 'Προφίλ', icon: User },
  ];

  // Sidebar nav items — new order
  const sidebarItems = [
    { id: 'home', label: 'Αρχική', icon: Home },
    { id: 'appointments', label: 'Ραντεβού', icon: Calendar },
    { id: 'loyalty', label: 'Elite Loyalty', icon: Crown, gold: true },
    { id: 'announcements', label: 'Ανακοινώσεις', icon: Bell, badge: unreadCount > 0 ? unreadCount : null },
    { id: 'products', label: 'Προϊόντα', icon: ShoppingBag },
    { id: 'about', label: 'Σχετικά', icon: Info },
    { id: 'profile', label: 'Προφίλ', icon: User },
    ...(user?.is_admin ? [{ id: 'admin', label: 'Admin', icon: Shield, red: true }] : []),
  ];

  const handleNav = (id) => {
    setActiveTab(id);
    setSidebarOpen(false);
    if (id === 'announcements') {
      localStorage.setItem(STORAGE_KEY, new Date().toISOString());
      setUnreadCount(0);
    }
    
    // Scroll to top of page
    window.scrollTo({ top: 0, behavior: 'instant' });
    
    // Scroll to the corresponding page for bottom tabs
    const tabOrder = ['home', 'appointments', 'loyalty', 'announcements', 'profile'];
    const index = tabOrder.indexOf(id);
    if (index !== -1) {
      setTimeout(() => {
        const container = document.querySelector('[data-testid="main-content"]');
        if (container) {
          const pageWidth = container.offsetWidth;
          container.scrollTo({ left: pageWidth * index, behavior: 'smooth' });
        }
      }, 0);
    }
  };

  // Heartbeat — update online status every 45s
  useEffect(() => {
    const sendHeartbeat = () => {
      axios.put(`${API}/users/heartbeat`, {}, { withCredentials: true }).catch(() => {});
    };
    sendHeartbeat();
    const hbInterval = setInterval(sendHeartbeat, 45000);
    const onVisChange = () => { if (!document.hidden) sendHeartbeat(); };
    document.addEventListener('visibilitychange', onVisChange);
    return () => {
      clearInterval(hbInterval);
      document.removeEventListener('visibilitychange', onVisChange);
    };
  }, []);

  // Swipe gesture: left edge → right opens sidebar, right → left closes it
  useEffect(() => {
    let startX = 0;
    let startY = 0;
    const onTouchStart = (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    };
    const onTouchEnd = (e) => {
      const dx = e.changedTouches[0].clientX - startX;
      const dy = Math.abs(e.changedTouches[0].clientY - startY);
      if (dy > 80) return; // ignore vertical swipes
      if (!sidebarOpen && startX < 30 && dx > 60) setSidebarOpen(true);
      if (sidebarOpen && dx < -60) setSidebarOpen(false);
    };
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [sidebarOpen]);

  // Calculate exact bubble position - centered on tab
  useEffect(() => {
    if (isDragging) return; // Don't update while dragging
    
    const updateBubblePosition = () => {
      const activeButton = document.querySelector(`[data-testid="btm-nav-${activeTab}"]`);
      const navBar = document.querySelector('[data-testid="bottom-nav"]')?.querySelector('div');
      
      if (activeButton && navBar) {
        const btnRect = activeButton.getBoundingClientRect();
        const navRect = navBar.getBoundingClientRect();
        
        // Calculate center position
        const btnCenter = btnRect.left + (btnRect.width / 2);
        const navLeft = navRect.left;
        const relativeCenter = btnCenter - navLeft;
        
        // All tabs same size now
        const bubbleWidth = btnRect.width;
        
        setBubblePosition({
          left: relativeCenter - (bubbleWidth / 2), // Center the bubble
          width: bubbleWidth,
        });
      }
    };
    
    // Update immediately - no delay
    requestAnimationFrame(updateBubblePosition);
    window.addEventListener('resize', updateBubblePosition);
    return () => window.removeEventListener('resize', updateBubblePosition);
  }, [activeTab, isDragging]);

  return (
    <div className="bg-black text-white font-body pb-20">
      {/* In-App Chat Notifications */}
      {inAppNotifications.map((notif, index) => (
        <div key={notif.id} style={{ top: `${80 + index * 110}px` }} className="fixed right-4 z-[10000]">
          <InAppNotification
            notification={notif}
            onClose={() => removeInAppNotification(notif.id)}
          />
        </div>
      ))}
      
      {/* Notification Banner */}
      {notifBanner && (
        <div className="bg-zinc-900 border-b border-white/5 px-5 py-3 flex items-center justify-between" data-testid="notif-banner">
          <div className="flex items-center space-x-3">
            <Bell className="w-4 h-4 text-zinc-500 flex-shrink-0" />
            <span className="text-sm text-zinc-400">Ενεργοποιήστε τις ειδοποιήσεις</span>
          </div>
          <div className="flex items-center space-x-3 ml-4 flex-shrink-0">
            <button onClick={handleEnableNotifications}
              className="text-xs bg-white text-black px-4 py-1.5 font-heading tracking-widest uppercase hover:bg-zinc-200 transition-colors"
              data-testid="enable-notif-btn">ΕΝΕΡΓΟΠΟΙΗΣΗ</button>
            <button onClick={handleDismissNotifBanner} className="text-zinc-600 hover:text-white transition-colors" data-testid="dismiss-notif-btn">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Header — minimal */}
      <header className="bg-black/90 backdrop-blur-xl border-b border-white/5 sticky top-0 z-50">
        <div className="px-5">
          <div className="flex items-center justify-between h-24 relative">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-1.5 text-zinc-500 hover:text-white transition-colors"
              data-testid="hamburger-btn"
              aria-label="Μενού"
            >
              <Menu className="w-5 h-5" />
            </button>
            <img
              src="/dimakas-logo.png"
              alt="DIMAKAS"
              className="h-28 w-auto cursor-pointer absolute left-1/2 -translate-x-1/2"
              onClick={() => handleNav('home')}
              data-testid="header-logo"
            />
            <div className="flex items-center gap-3">
              {user?.is_admin && (
                <button
                  onClick={() => handleNav('admin')}
                  className="text-[10px] bg-admin-red/20 text-admin-red px-2 py-1 font-heading tracking-widest uppercase border border-admin-red/30 hover:bg-admin-red/30 transition-colors cursor-pointer"
                  data-testid="admin-quick-btn"
                >ADMIN</button>
              )}
              <Avatar 
                user={user} 
                size="sm" 
                onClick={() => handleNav('profile')}
                testId="profile-avatar"
              />
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar overlay */}
      <div
        className="fixed inset-0 z-[60]"
        style={{
          background: 'rgba(0,0,0,0.7)',
          backdropFilter: 'blur(8px)',
          opacity: sidebarOpen ? 1 : 0,
          pointerEvents: sidebarOpen ? 'auto' : 'none',
          transition: 'opacity 0.3s ease',
        }}
        onClick={() => setSidebarOpen(false)}
        data-testid="sidebar-overlay"
      />

      {/* Sidebar drawer */}
      <aside
        className="fixed top-0 left-0 h-full z-[70] w-72 bg-black border-r border-white/5 flex flex-col"
        style={{
          transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
        }}
        data-testid="sidebar-drawer"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <img src="/dimakas-logo.png" alt="DIMAKAS" className="h-8 w-auto" />
          <div className="flex items-center gap-3">
            <MusicButton />
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-zinc-600 hover:text-white transition-colors"
              data-testid="sidebar-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
          {sidebarItems.map(({ id, label, icon: Icon, gold, red, badge }) => {
            const isActive = activeTab === id;
            return (
              <button
                key={id}
                onClick={() => handleNav(id)}
                className={`w-full text-left px-4 py-3 text-sm font-heading tracking-[0.12em] uppercase transition-all flex items-center gap-3 ${
                  isActive
                    ? red ? 'text-admin-red bg-admin-red/10' : gold ? 'text-gold bg-gold/10' : 'text-white bg-white/5'
                    : red ? 'text-admin-red/60 hover:text-admin-red hover:bg-admin-red/5' : gold ? 'text-brand-dark hover:text-brand hover:bg-brand/5' : 'text-zinc-600 hover:text-zinc-300 hover:bg-white/[0.03]'
                }`}
                style={{
                  borderLeft: isActive
                    ? `2px solid ${red ? '#EF4444' : gold ? GOLD : '#fff'}`
                    : '2px solid transparent',
                }}
                data-testid={`nav-${id}`}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                <span className="flex-1">{label}</span>
                {badge && (
                  <span
                    className="text-[10px] font-body font-medium px-1.5 py-0.5 min-w-[18px] text-center rounded-sm"
                    style={{ color: GOLD, background: GOLD + '15', border: `1px solid ${GOLD}30` }}
                    data-testid={`nav-${id}-badge`}
                  >
                    {badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>


        {/* Bottom Section - Install App Button */}
        <div className="px-3 py-3 border-t border-white/10">
          <button
            onClick={handleInstallClick}
            className="w-full px-4 py-2.5 rounded-lg transition-all duration-200 flex items-center justify-center gap-2.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-600 hover:border-zinc-500"
            style={{ color: BRAND }}
            data-testid="sidebar-install-app-button"
          >
            <Download className="w-4 h-4" />
            <span className="text-sm font-medium tracking-wide">
              Εγκατάσταση εφαρμογής
            </span>
          </button>
        </div>

        <div className="px-5 py-4 border-t border-white/5 pb-3">
          <p className="text-zinc-800 text-[10px] font-heading tracking-[0.2em] uppercase">
            DIMAKAS · Modern Male Image
          </p>
        </div>
      </aside>

      {/* Page content — bottom padding for nav bar */}
      <main 
        ref={(el) => {
          if (!el) return;
          const container = el;
          let ticking = false;
          
          const handleScroll = () => {
            if (!ticking) {
              requestAnimationFrame(() => {
                const scrollLeft = container.scrollLeft;
                const pageWidth = container.offsetWidth;
                const maxScroll = pageWidth * 4; // 5 pages
                const scrollProgress = Math.min(1, scrollLeft / maxScroll);
                
                // Update bubble position based on scroll
                const tabs = ['home', 'appointments', 'loyalty', 'announcements', 'profile'];
                const tabIndex = scrollProgress * 4; // 0 to 4
                const currentIndex = Math.round(scrollLeft / pageWidth);
                
                // Calculate smooth bubble position during scroll
                if (!isDragging) {
                  const navBar = document.querySelector('[data-testid="bottom-nav"]')?.querySelector('div');
                  if (navBar) {
                    const allButtons = tabs.map(id => document.querySelector(`[data-testid="btm-nav-${id}"]`)).filter(Boolean);
                    if (allButtons.length === 5) {
                      const navRect = navBar.getBoundingClientRect();
                      
                      // Interpolate between tabs
                      const floorIndex = Math.floor(tabIndex);
                      const ceilIndex = Math.ceil(tabIndex);
                      const fraction = tabIndex - floorIndex;
                      
                      const btn1 = allButtons[floorIndex];
                      const btn2 = allButtons[Math.min(ceilIndex, 4)];
                      
                      if (btn1 && btn2) {
                        const rect1 = btn1.getBoundingClientRect();
                        const rect2 = btn2.getBoundingClientRect();
                        
                        const left1 = rect1.left - navRect.left + (rect1.width / 2);
                        const left2 = rect2.left - navRect.left + (rect2.width / 2);
                        const width1 = rect1.width;
                        const width2 = rect2.width;
                        
                        const interpolatedLeft = left1 + (left2 - left1) * fraction;
                        const interpolatedWidth = width1 + (width2 - width1) * fraction;
                        
                        setBubblePosition({
                          left: interpolatedLeft - (interpolatedWidth / 2),
                          width: interpolatedWidth,
                        });
                      }
                    }
                  }
                }
                
                // Update active tab
                if (tabs[currentIndex] && activeTab !== tabs[currentIndex]) {
                  setActiveTab(tabs[currentIndex]);
                }
                
                ticking = false;
              });
              ticking = true;
            }
          };
          
          container.addEventListener('scroll', handleScroll, { passive: true });
          return () => container.removeEventListener('scroll', handleScroll);
        }}
        className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar pb-24"
        style={{ 
          scrollBehavior: 'smooth',
          WebkitOverflowScrolling: 'touch',
        }}
        data-testid="main-content"
      >
        <div className="min-w-full snap-start px-5 py-6" style={{ visibility: activeTab === 'home' ? 'visible' : 'hidden' }}>
          <HomePage isAdmin={user?.is_admin} onNavigateToAppointments={() => handleNav('appointments')} />
        </div>
        <div className="min-w-full snap-start px-5 py-6" style={{ visibility: activeTab === 'appointments' ? 'visible' : 'hidden' }}>
          <AppointmentsPage />
        </div>
        <div className="min-w-full snap-start px-5 py-6" style={{ visibility: activeTab === 'loyalty' ? 'visible' : 'hidden' }}>
          {activeTab === 'loyalty' && <RewardShop onClose={() => {}} />}
        </div>
        <div className="min-w-full snap-start px-5 py-6" style={{ visibility: activeTab === 'announcements' ? 'visible' : 'hidden' }}>
          <AnnouncementsPage isAdmin={user?.is_admin} currentUserId={user?.user_id} />
        </div>
        <div className="min-w-full snap-start px-5 py-6" style={{ visibility: activeTab === 'profile' ? 'visible' : 'hidden' }}>
          <ProfilePage />
        </div>
      </main>

      {/* Hidden pages (admin, products, about) - accessible via sidebar only */}
      {(activeTab === 'products' || activeTab === 'about' || activeTab === 'admin') && (
        <div className="fixed inset-0 z-[70] bg-black overflow-y-auto">
          <div className="min-h-screen px-5 py-6 pb-24">
            <button
              onClick={() => handleNav('home')}
              className="mb-4 text-white hover:text-gray-300 transition-colors"
            >
              ← Πίσω
            </button>
            {activeTab === 'products' && <ProductsPage isAdmin={user?.is_admin} />}
            {activeTab === 'about' && <AboutPage isAdmin={user?.is_admin} />}
            {activeTab === 'admin' && user?.is_admin && <AdminPanel />}
          </div>
        </div>
      )}

      {/* Bottom Navigation Bar — Minimal Clean Theme */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 flex justify-center pb-3 px-3 pointer-events-none" data-testid="bottom-nav">
        <div
          className="pointer-events-auto relative flex items-end justify-around w-full max-w-md rounded-[24px] px-2 pt-2 pb-2"
          style={{
            background: 'rgba(10,10,10,0.85)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: '1px solid rgba(255,255,255,0.06)',
            boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
          }}
        >
          {/* Premium Indicator Bubble - Instant movement with scroll */}
          <div
            className="absolute cursor-grab active:cursor-grabbing"
            style={{
              left: 0,
              top: '0',
              width: `${bubblePosition.width}px`,
              height: '100%',
              transform: `translateX(${bubblePosition.left}px)`,
              background: 'rgba(255,255,255,0.12)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              borderRadius: '20px',
              border: '1px solid rgba(255,255,255,0.15)',
              boxShadow: '0 2px 12px rgba(255,255,255,0.08)',
              zIndex: 0,
              transition: 'none', // No CSS transition - let scroll handle it
              pointerEvents: 'auto',
            }}
            onTouchStart={(e) => {
              setIsDragging(true);
              const touch = e.touches[0];
              const startX = touch.clientX;
              const startLeft = bubblePosition.left;
              const navBar = e.currentTarget.parentElement;
              const navRect = navBar.getBoundingClientRect();
              
              const handleTouchMove = (e) => {
                e.preventDefault();
                const touch = e.touches[0];
                const currentX = touch.clientX - navRect.left;
                
                // Update bubble position to follow finger
                setBubblePosition(prev => ({
                  ...prev,
                  left: Math.max(0, Math.min(navRect.width - prev.width, currentX - prev.width / 2))
                }));
                
                // Find which tab we're over
                const buttons = Array.from(navBar.querySelectorAll('[data-testid^="btm-nav-"]'));
                buttons.forEach(btn => {
                  const btnRect = btn.getBoundingClientRect();
                  const btnCenterX = (btnRect.left + btnRect.right) / 2;
                  const bubbleCenterX = touch.clientX;
                  
                  if (Math.abs(btnCenterX - bubbleCenterX) < btnRect.width / 2) {
                    const tabId = btn.getAttribute('data-testid').replace('btm-nav-', '');
                    if (tabId !== activeTab) {
                      handleNav(tabId);
                    }
                  }
                });
              };
              
              const handleTouchEnd = () => {
                setIsDragging(false);
                window.removeEventListener('touchmove', handleTouchMove);
                window.removeEventListener('touchend', handleTouchEnd);
              };
              
              window.addEventListener('touchmove', handleTouchMove, { passive: false });
              window.addEventListener('touchend', handleTouchEnd);
            }}
          />
          
          {bottomTabs.map(({ id, label, icon: Icon, gold, badge, center }, index) => {
            const isActive = activeTab === id;
            const color = gold
              ? (isActive ? GOLD : '#707981')
              : (isActive ? '#fff' : '#52525B');

            if (center) {
              return (
                <button
                  key={id}
                  onClick={() => handleNav(id)}
                  className="relative flex flex-col items-center -mt-6 mx-1 active:scale-95 transition-transform z-10"
                  data-testid={`btm-nav-${id}`}
                >
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center mb-1"
                    style={{
                      background: isActive
                        ? 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.04) 100%)'
                        : 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)',
                      border: '1px solid rgba(255,255,255,0.12)',
                      boxShadow: isActive
                        ? '0 0 16px rgba(255,255,255,0.08), 0 4px 16px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)'
                        : '0 4px 12px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.06)',
                    }}
                  >
                    <Icon className="w-6 h-6" style={{ color: isActive ? '#fff' : 'rgba(255,255,255,0.5)' }} />
                  </div>
                  <span
                    className="text-[9px] font-heading tracking-wider uppercase"
                    style={{ color: isActive ? '#fff' : 'rgba(255,255,255,0.4)' }}
                  >
                    {label}
                  </span>
                </button>
              );
            }

            return (
              <button
                key={id}
                onClick={() => handleNav(id)}
                className="relative flex flex-col items-center justify-center gap-1 flex-1 py-1.5 z-10"
                data-testid={`btm-nav-${id}`}
              >
                <div className="relative">
                  <Icon className="w-5 h-5 transition-colors" style={{ color }} />
                  {badge && (
                    <span
                      className="absolute -top-1.5 -right-2.5 text-[9px] font-medium px-1 min-w-[14px] text-center rounded-full bg-white text-black"
                      data-testid={`btm-nav-${id}-badge`}
                    >
                      {badge}
                    </span>
                  )}
                </div>
                <span
                  className="text-[9px] font-heading tracking-wider uppercase transition-colors"
                  style={{ color }}
                >
                  {label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
      {/* Install Modal */}
      <InstallModal 
        isOpen={showInstallModal} 
        onClose={() => setShowInstallModal(false)} 
      />
    </div>
  );
}

export default Dashboard;
